/* eslint-disable @typescript-eslint/no-explicit-any */
import { chunk, compact, flatten, noop } from 'lodash';

import { titleCase } from 'dashboard/lib/utils/stringFormat';
import {
  DatumType,
  DatumTypeToken,
  G6NodeId,
  HashableNodeId,
  NodeDataTypeIcon,
  NodeId,
  nodeIdToHashable,
  V2Data,
} from 'dashboard/models/graph';
import { Backend } from 'dashboard/models/graph-backend';
import { DemoBackend } from 'dashboard/models/graph-demo-backend';
import { GraphExplorer } from 'dashboard/models/graph-explorer';
import { convertToHydratedNode, HydratedNode } from 'dashboard/models/graph-hydration';
import addressIconSrc from 'images/icons/graph/address.svg?inline';
import alertCircleIconSrc from 'images/icons/graph/alert-circle.svg?inline';
import apiIconSrc from 'images/icons/graph/api.svg?inline';
import birthdateIconSrc from 'images/icons/graph/calendar.svg?inline';
import casesIconSrc from 'images/icons/graph/cases.svg?inline';
import circleInfoIconSrc from 'images/icons/graph/circle-info.svg?inline';
import clipboardIconSrc from 'images/icons/graph/clipboard.svg?inline';
import clockIconSrc from 'images/icons/graph/clock.svg?inline';
import documentationIconSrc from 'images/icons/graph/documentation.svg?inline';
import editIconSrc from 'images/icons/graph/edit.svg?inline';
import emailIconSrc from 'images/icons/graph/email.svg?inline';
import eventsIconSrc from 'images/icons/graph/events.svg?inline';
import fileTextIconSrc from 'images/icons/graph/file-text.svg?inline';
import ipAddressIconSrc from 'images/icons/graph/globe.svg?inline';
import governmentIconSrc from 'images/icons/graph/government.svg?inline';
import imageIconSrc from 'images/icons/graph/image.svg?inline';
import identificationNumberIconSrc from 'images/icons/graph/inquiries.svg?inline';
import integrationIconSrc from 'images/icons/graph/integration.svg?inline';
import listIconSrc from 'images/icons/graph/list-layout.svg?inline';
import fingerprintIconSrc from 'images/icons/graph/mac.svg?inline';
import nameIconSrc from 'images/icons/graph/name.svg?inline';
import phoneNumberIconSrc from 'images/icons/graph/phone.svg?inline';
import postalCodeIconSrc from 'images/icons/graph/pin.svg?inline';
import shieldIconSrc from 'images/icons/graph/shield.svg?inline';
import similarityIconSrc from 'images/icons/graph/similarity.svg?inline';
import smileIconSrc from 'images/icons/graph/smile.svg?inline';
import starIconSrc from 'images/icons/graph/star.svg?inline';
import teamIconSrc from 'images/icons/graph/team.svg?inline';
import toolIconSrc from 'images/icons/graph/tool.svg?inline';
import transactionIconSrc from 'images/icons/graph/transaction.svg?inline';
import trashIconSrc from 'images/icons/graph/trash.svg?inline';
import unknownIconSrc from 'images/icons/graph/unknown.svg?inline';
// This is in `utils` instead of `constants` to reflect the cost of importing many image assets
// compared to typical `constants` imports.
// Leading double bang means "disable all loaders that would be used for SVG"
// url-loader! means prepend the URL loader for this import only
// url-loader transforms assets into base64, very efficient for small icons etc.
import accountIconSrc from 'images/icons/graph/user.svg?inline';
import webhooksIconSrc from 'images/icons/graph/webhooks.svg?inline';
import workflowsIconSrc from 'images/icons/graph/workflows.svg?inline';
// This is in `utils` instead of `constants` to reflect the cost of importing many image assets
// compared to typical `constants` imports.
import {
  ACCOUNT_DATUM_TYPE,
  ACCOUNT_SIMILARITY_SELFIE_DATUM_TYPE,
  ADDRESS_DATUM_TYPE,
  BIRTHDATE_DATUM_TYPE,
  BROWSER_FINGERPRINT_DATUM_TYPE,
  DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE,
  DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE,
  DEVICE_TOKEN_DATUM_TYPE,
  EMAIL_DATUM_TYPE,
  EXTERNAL_ACCOUNT_DATUM_TYPE,
  GOVERNMENT_ID_DATUM_TYPE,
  ID_DOCUMENT_NUMBER_DATUM_TYPE,
  ID_NUMBER_DATUM_TYPE,
  NAME_DATUM_TYPE,
  NETWORK_DATUM_TYPE,
  PERSONA_DATUM_TYPES,
  PHONE_NUMBER_DATUM_TYPE,
  POSTAL_CODE_DATUM_TYPE,
  SOCIAL_SECURITY_NUMBER_DATUM_TYPE,
  SOCIAL_SECURITY_NUMBER_LAST_4_DATUM_TYPE,
  VertexType,
} from 'lib/constants/graph';

// -----------------------------------------------------------------------------------------
// Icons
// -----------------------------------------------------------------------------------------

// These icons are expected to have square dimensions (aspect ratio == 1)
export const VERTEX_ICON_SOURCES: Record<VertexType, string | null> = {
  [VertexType.Account]: accountIconSrc as any as string,
  [VertexType.Address]: addressIconSrc as any as string,
  [VertexType.Birthdate]: birthdateIconSrc as any as string,
  [VertexType.BrowserFingerprint]: fingerprintIconSrc as any as string,
  [VertexType.DeviceFingerprint]: fingerprintIconSrc as any as string,
  [VertexType.EmailAddress]: emailIconSrc as any as string,
  [VertexType.GovernmentIdNumber]: identificationNumberIconSrc as any as string,
  [VertexType.IdentificationNumber]: identificationNumberIconSrc as any as string,
  [VertexType.Network]: ipAddressIconSrc as any as string,
  [VertexType.Name]: nameIconSrc as any as string,
  [VertexType.PhoneNumber]: phoneNumberIconSrc as any as string,
  [VertexType.PostalCode]: postalCodeIconSrc as any as string,
  [VertexType.SocialSecurityNumber]: identificationNumberIconSrc as any as string,
  [VertexType.SocialSecurityNumberLast4]: identificationNumberIconSrc as any as string,
  [VertexType.Unknown]: null,
} as const;

// These icons are expected to have square dimensions (aspect ratio == 1)
/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
export const VERTEX_ICON_SOURCES_V2: Record<DatumTypeToken | null, string | null> = {
  [ACCOUNT_DATUM_TYPE.token]: accountIconSrc as any as string,
  [ACCOUNT_SIMILARITY_SELFIE_DATUM_TYPE.token]: smileIconSrc as any as string,
  [ADDRESS_DATUM_TYPE.token]: addressIconSrc as any as string,
  [BIRTHDATE_DATUM_TYPE.token]: birthdateIconSrc as any as string,
  [BROWSER_FINGERPRINT_DATUM_TYPE.token]: fingerprintIconSrc as any as string,
  [DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE.token]: fingerprintIconSrc as any as string,
  [DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE.token]: fingerprintIconSrc as any as string,
  [DEVICE_TOKEN_DATUM_TYPE.token]: fingerprintIconSrc as any as string,
  [EMAIL_DATUM_TYPE.token]: emailIconSrc as any as string,
  [GOVERNMENT_ID_DATUM_TYPE.token]: identificationNumberIconSrc as any as string,
  [ID_DOCUMENT_NUMBER_DATUM_TYPE.token]: identificationNumberIconSrc as any as string,
  [ID_NUMBER_DATUM_TYPE.token]: identificationNumberIconSrc as any as string,
  [NAME_DATUM_TYPE.token]: nameIconSrc as any as string,
  [NETWORK_DATUM_TYPE.token]: ipAddressIconSrc as any as string,
  [PHONE_NUMBER_DATUM_TYPE.token]: phoneNumberIconSrc as any as string,
  [POSTAL_CODE_DATUM_TYPE.token]: postalCodeIconSrc as any as string,
  [SOCIAL_SECURITY_NUMBER_DATUM_TYPE.token]: identificationNumberIconSrc as any as string,
  [SOCIAL_SECURITY_NUMBER_LAST_4_DATUM_TYPE.token]: identificationNumberIconSrc as any as string,
  // Transactions Demo datum types.
  [EXTERNAL_ACCOUNT_DATUM_TYPE.token]: accountIconSrc as any as string,
} as const;

export const LIST_ICON_SOURCE = listIconSrc as any as string;

export const NODE_ICON_SOURCES: Record<NodeDataTypeIcon, string> = {
  [NodeDataTypeIcon.Account]: accountIconSrc as any as string,
  [NodeDataTypeIcon.Address]: addressIconSrc as any as string,
  [NodeDataTypeIcon.AlertCircle]: alertCircleIconSrc as any as string,
  [NodeDataTypeIcon.Api]: apiIconSrc as any as string,
  [NodeDataTypeIcon.Calendar]: birthdateIconSrc as any as string,
  [NodeDataTypeIcon.Cases]: casesIconSrc as any as string,
  [NodeDataTypeIcon.CircleInfo]: circleInfoIconSrc as any as string,
  [NodeDataTypeIcon.Clipboard]: clipboardIconSrc as any as string,
  [NodeDataTypeIcon.Clock]: clockIconSrc as any as string,
  [NodeDataTypeIcon.Documentation]: documentationIconSrc as any as string,
  [NodeDataTypeIcon.Edit]: editIconSrc as any as string,
  [NodeDataTypeIcon.Email]: emailIconSrc as any as string,
  [NodeDataTypeIcon.Events]: eventsIconSrc as any as string,
  [NodeDataTypeIcon.FileText]: fileTextIconSrc as any as string,
  [NodeDataTypeIcon.Globe]: ipAddressIconSrc as any as string,
  [NodeDataTypeIcon.Government]: governmentIconSrc as any as string,
  [NodeDataTypeIcon.Image]: imageIconSrc as any as string,
  [NodeDataTypeIcon.Inquiries]: identificationNumberIconSrc as any as string,
  [NodeDataTypeIcon.Integration]: integrationIconSrc as any as string,
  [NodeDataTypeIcon.Phone]: phoneNumberIconSrc as any as string,
  [NodeDataTypeIcon.Shield]: shieldIconSrc as any as string,
  [NodeDataTypeIcon.Star]: starIconSrc as any as string,
  [NodeDataTypeIcon.Team]: teamIconSrc as any as string,
  [NodeDataTypeIcon.Tool]: toolIconSrc as any as string,
  [NodeDataTypeIcon.Trash]: trashIconSrc as any as string,
  [NodeDataTypeIcon.Webhooks]: webhooksIconSrc as any as string,
  [NodeDataTypeIcon.Workflows]: workflowsIconSrc as any as string,
  [NodeDataTypeIcon.Name]: nameIconSrc as any as string,
  [NodeDataTypeIcon.Fingerprint]: fingerprintIconSrc as any as string,
  [NodeDataTypeIcon.Postal]: postalCodeIconSrc as any as string,
  [NodeDataTypeIcon.Similarity]: similarityIconSrc as any as string,
  [NodeDataTypeIcon.Smile]: smileIconSrc as any as string,
  [NodeDataTypeIcon.Transaction]: transactionIconSrc as any as string,
  [NodeDataTypeIcon.Unknown]: unknownIconSrc as any as string,
} as const;

// -----------------------------------------------------------------------------------------
// Mock Data
// -----------------------------------------------------------------------------------------

export enum GraphExplorerMockDataKey {
  Accounts = 'graphExplorer:accountsMockData',
  Clusters = 'graphExplorer:clustersMockData',
  Hydration = 'graphExplorer:hydrationMockData',
  DebugMode = 'graphExplorer:debugMode',
  Expansion = 'graphExplorer:expansionMockData',

  DemoBackend = 'graphExplorer:demoBackendData',
}

const setExplorerAccountsMockData = (json: V2Data) => {
  window.localStorage.setItem(GraphExplorerMockDataKey.Accounts, JSON.stringify(json));
};

const setExplorerClustersMockData = (json: V2Data) => {
  window.localStorage.setItem(GraphExplorerMockDataKey.Clusters, JSON.stringify(json));
};

const listDatumTypes = () => {
  const table: Record<string, DatumType> = {};
  PERSONA_DATUM_TYPES.forEach((datumType) => {
    table[datumType.name] = datumType;
  });
  // eslint-disable-next-line no-console
  console.table(table);
};

const setExplorerHydrationNodeMockData = (json: Record<G6NodeId, HydratedNode>) => {
  window.localStorage.setItem(GraphExplorerMockDataKey.Hydration, JSON.stringify(json));
};

const setDebugMode = (mode: boolean) => {
  window.localStorage.setItem(GraphExplorerMockDataKey.DebugMode, JSON.stringify(mode));
  return mode;
};

export const getDebugMode = () => {
  return JSON.parse(window.localStorage.getItem(GraphExplorerMockDataKey.DebugMode) || 'false');
};

const setExplorerExpansionMockData = (json: Record<G6NodeId, V2Data>) => {
  window.localStorage.setItem(GraphExplorerMockDataKey.Expansion, JSON.stringify(json));
};
/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-ignore
window.__graphDemoBackend = DemoBackend;
// @ts-ignore
window.__setExplorerAccountsMockData = setExplorerAccountsMockData;
// @ts-ignore
window.__setExplorerClustersMockData = setExplorerClustersMockData;
// @ts-ignore
window.__setExplorerHydrationNodeData = setExplorerHydrationNodeMockData;
// @ts-ignore
window.__listDatumTypes = listDatumTypes;
// @ts-ignore
window.__graphDebugModeEnable = setDebugMode.bind(null, true);
// @ts-ignore
window.__graphDebugModeDisable = setDebugMode.bind(null, false);
// @ts-ignore
window.__graphDebugMode = getDebugMode;
// @ts-ignore
window.__setExplorerExpansionMockData = setExplorerExpansionMockData;
/* eslint-enable @typescript-eslint/ban-ts-comment */

// -----------------------------------------------------------------------------------------
// Display Utils
// -----------------------------------------------------------------------------------------
export const parseLabelFromNodeId = (nodeId: NodeId): string => {
  // attempts to parse nodeId.value as JSON for callback to use. If not possible, return nodeId.value as is
  /* eslint-disable @typescript-eslint/no-explicit-any */
  if (!nodeId) {
    return '';
  }

  const withParsedJSON = (cb: (parsedJSON: any) => string): string => {
    try {
      const parsed = JSON.parse(nodeId.value);
      return cb(parsed);
    } catch (e) {
      return nodeId.value;
    }
  };

  switch (nodeId.token) {
    case NAME_DATUM_TYPE.token: {
      return withParsedJSON((parsedJSON) =>
        [parsedJSON['name_first'], parsedJSON['name_middle'], parsedJSON['name_last']]
          .filter(Boolean)
          .map(titleCase)
          .join(' '),
      );
    }
    case ADDRESS_DATUM_TYPE.token: {
      return withParsedJSON((parsedJSON) =>
        // grabs address_line1 to address_12
        [...Array(13).keys()]
          .map((i) => parsedJSON[`address_line${i}`])
          .filter(Boolean)
          .join(', '),
      );
    }
    case ACCOUNT_SIMILARITY_SELFIE_DATUM_TYPE.token: {
      return nodeId.value.replace('-', ' to ');
    }
    default:
      try {
        const nodeIdvalue = JSON.parse(nodeId.value);
        if (typeof nodeIdvalue !== 'object') {
          return nodeId.value;
        }
        return Object.entries(nodeIdvalue)
          .map(([key, value]) => `${key}: ${value}`)
          .join(', ');
      } catch (e) {
        // If we can't parse the JSON (like when node_key_schema.size==1), just show the raw value.
        return nodeId.value;
      }
  }
};

export const getHydratedNodeDisplayValue = (hydratedNode: HydratedNode): string => {
  switch (hydratedNode.nodeType) {
    case 'account_status':
      return hydratedNode.hydrationData.status;
    case 'account_type':
      return hydratedNode.hydrationData.type;
    case 'address':
      return [
        hydratedNode.hydrationData['address-line1'],
        hydratedNode.hydrationData['address-line2'],
        hydratedNode.hydrationData['address-line3'],
      ].join(' ');
    case 'birthdate':
      return hydratedNode.hydrationData.birthdate;
    case 'browser_fingerprint':
      return hydratedNode.hydrationData['browser-fingerprint'];
    case 'device_fingerprint':
      return hydratedNode.hydrationData['device-fingerprint'];
    case 'device_token':
      return hydratedNode.hydrationData.token;
    case 'device_vendor_id':
      return hydratedNode.hydrationData['device-vendor-id'];
    case 'email':
      return hydratedNode.hydrationData.email;
    case 'government_id':
      return hydratedNode.hydrationData.id;
    case 'id_document_number':
      return hydratedNode.hydrationData['document-number'];
    case 'id_number':
      return hydratedNode.hydrationData.id;
    case 'name':
      return [
        hydratedNode.hydrationData['name-first'],
        hydratedNode.hydrationData['name-middle'],
        hydratedNode.hydrationData['name-last'],
      ]
        .filter(Boolean)
        .join(' ');
    case 'network':
      return hydratedNode.hydrationData.ip;
    case 'phone':
      return hydratedNode.hydrationData.phone;
    case 'postal_code':
      return hydratedNode.hydrationData['postal-code'];
    case 'ssn':
      return hydratedNode.hydrationData.ssn;
    case 'ssn_last4':
      return hydratedNode.hydrationData['ssn-last4'];
    case 'custom_datum_type':
      return hydratedNode.hydrationData.value;
    default:
      return hydratedNode.nodeType;
  }
};

export const hydrateInBatches = (
  nodeIds: NodeId[],
  graphId: string,
  backend: Backend,
  graphExplorer: GraphExplorer,
  getShouldSkip: (batch: NodeId[]) => boolean = () => false,
  onBatchResult: (batch: NodeId[], result: HydratedNode[]) => void = noop,
  includeAccountFields = false,
  batchSize = 100,
  throttleIntervalMs = 200,
): Promise<Map<HashableNodeId, HydratedNode>> => {
  const batches = chunk(nodeIds, batchSize);
  const hydratedPairsInBatches = batches.map((batch, idx) => {
    let shouldSkip = false;
    const getHydratedNodes = async () => {
      shouldSkip = getShouldSkip(batch);
      if (shouldSkip) {
        return;
      }
      const response = await backend.hydrate({
        nodeIds: batch,
        graphId: graphId,
        includeAccountFields,
      });
      const serverHydratedNodes = response['hydrated-nodes'];
      if (serverHydratedNodes.length !== batch.length) {
        throw new Error('unexpected server hydration response');
      }

      const hydratedNodes = serverHydratedNodes.map((serverHydratedNode, idx) => {
        const nodeId = batch[idx];
        return convertToHydratedNode(
          graphExplorer.getDatumTypeByToken(nodeId.token),
          serverHydratedNode,
        );
      });

      onBatchResult(batch, hydratedNodes);
      return batch.map((nodeId, i) => {
        return { nodeId, hydratedNode: hydratedNodes[i] };
      });
    };

    if (shouldSkip) {
      return;
    }

    if (idx !== 0 && idx !== batches.length - 1) {
      setTimeout(noop, throttleIntervalMs);
    }

    return getHydratedNodes();
  });

  return Promise.all(hydratedPairsInBatches).then((hydratedPairs) => {
    const nodeIdtoHydratedNode = new Map<HashableNodeId, HydratedNode>();
    compact(flatten(hydratedPairs)).forEach(({ nodeId, hydratedNode }) => {
      nodeIdtoHydratedNode.set(nodeIdToHashable(nodeId), hydratedNode);
    });
    return nodeIdtoHydratedNode;
  });
};
